import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AuraCastProjectionPage() {
  const { auraKey } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const interval = parseInt(params.get('interval') || '10', 10) * 1000;
  const transition = params.get('transition') || 'fade'; // fade|slide|ken-burns
  const order = params.get('order') || 'random'; // random|chrono|newest
  const [media, setMedia] = useState([]);
  const [idx, setIdx] = useState(0);
  const [spotlight, setSpotlight] = useState(null);
  const [fsHint, setFsHint] = useState(true);
  const [cursorHidden, setCursorHidden] = useState(false);
  const cursorTimer = useRef(null);
  const wakeLockRef = useRef(null);

  const sortMedia = useCallback((list) => {
    const arr = [...list];
    if (order === 'chrono') arr.sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at));
    else if (order === 'newest') arr.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
    else arr.sort(() => Math.random() - 0.5);
    return arr;
  }, [order]);

  const fetchMedia = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auracast/${auraKey}`, { withCredentials: true });
      setMedia(sortMedia((res.data?.media || []).filter(m => m.file_type === 'image' || m.file_type === 'video')));
    } catch (e) { /* ignore */ }
  }, [auraKey, sortMedia]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);
  // File links are signed and expire; a projector left on all night re-reads
  // the list every 30 minutes so it never shows a stale link.
  useEffect(() => {
    const t = setInterval(fetchMedia, 30 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchMedia]);

  // Wake Lock
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    requestWakeLock();
    const onVis = () => { if (document.visibilityState === 'visible') requestWakeLock(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [requestWakeLock]);

  // Cursor auto-hide
  useEffect(() => {
    const onMove = () => {
      setCursorHidden(false);
      if (cursorTimer.current) clearTimeout(cursorTimer.current);
      cursorTimer.current = setTimeout(() => setCursorHidden(true), 3000);
    };
    window.addEventListener('mousemove', onMove);
    onMove();
    return () => { window.removeEventListener('mousemove', onMove); if (cursorTimer.current) clearTimeout(cursorTimer.current); };
  }, []);

  // Rotation
  useEffect(() => {
    if (!media.length || spotlight) return;
    const t = setTimeout(() => setIdx(i => (i + 1) % media.length), interval);
    return () => clearTimeout(t);
  }, [media, idx, interval, spotlight]);

  // WS subscribe
  useEffect(() => {
    if (!auraKey) return;
    const wsUrl = (API_URL || window.location.origin).replace(/^http/, 'ws') + `/api/ws/auracast/${auraKey}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.event === 'media_added' && data.media) {
            setSpotlight(data.media);
            setTimeout(() => { setSpotlight(null); fetchMedia(); }, 8000);
          }
        } catch (_) { /* ignore */ }
      };
    } catch (e) { /* ignore */ }
    return () => { try { ws && ws.close(); } catch (_) {} };
  }, [auraKey, fetchMedia]);

  const enterFullscreen = async () => {
    try { await document.documentElement.requestFullscreen(); setFsHint(false); } catch (e) { setFsHint(false); }
  };

  useEffect(() => {
    // Try silently first; many browsers reject without gesture, so we keep the hint.
    const t = setTimeout(() => {
      document.documentElement.requestFullscreen?.().then(() => setFsHint(false)).catch(() => {});
    }, 100);
    return () => clearTimeout(t);
  }, []);

  const current = spotlight || media[idx];
  const transClass = transition === 'slide' ? 'animate-slide-in' : transition === 'ken-burns' ? 'animate-ken-burns' : 'animate-fade-in';

  return (
    <div className={`min-h-screen w-screen bg-black flex items-center justify-center overflow-hidden ${cursorHidden ? 'cursor-none' : ''}`}>
      <button
        onClick={async () => {
          try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (_) {}
          if (window.history.length > 1) navigate(-1);
          else navigate(`/portal/${auraKey}`);
        }}
        className="absolute top-4 left-4 z-50 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm"
        aria-label="Exit projection"
      >
        ← Exit
      </button>
      {fsHint && (
        <button onClick={enterFullscreen} aria-label="Enter fullscreen" className="absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm">
          Click to enter fullscreen
        </button>
      )}
      {!current && (
        <div className="text-white/60 text-2xl font-serif">Waiting for the first photo…</div>
      )}
      {current && current.file_type === 'image' && (
        <img key={current.id} src={`${API_URL}${current.url}`} alt={current.caption || 'Aura photo'}
             className={`max-h-screen max-w-screen object-contain ${transClass}`} />
      )}
      {current && current.file_type === 'video' && (
        <video key={current.id} src={`${API_URL}${current.url}`} autoPlay muted playsInline
               className={`max-h-screen max-w-screen ${transClass}`} />
      )}
      {spotlight && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-champagne/90 text-midnight px-6 py-3 rounded-full font-medium">
          ✨ New from {spotlight.guest_name || 'a guest'}
        </div>
      )}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        .animate-fade-in { animation: fadeIn 1s ease }
        @keyframes slideIn { from { transform: translateX(100%) } to { transform: translateX(0) } }
        .animate-slide-in { animation: slideIn 0.8s ease }
        @keyframes kenBurns { from { transform: scale(1) } to { transform: scale(1.08) } }
        .animate-ken-burns { animation: kenBurns 12s ease both }
      `}</style>
    </div>
  );
}
